import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSiteAdminAuth } from '@shared/providers/SiteAdminAuthContext';
import { useAdminTheme } from '@shared/providers/admin-theme/AdminThemeContext';
import { getSiteAdminTheme } from '@shared/providers/admin-theme/configs';

/**
 * Site Admin Header Props
 */
interface SiteAdminHeaderProps {
  onMenuClick?: () => void;
}

/**
 * Site Admin Header Component
 *
 * Displayed site name (`siteName`) is taken directly from URL param `:site`.
 * site = 'super-admin' → "Super Admin 管理ポータル". When there is no URL site
 * (e.g. another route) fallback `themeConfig.siteName`.
 */
export const SiteAdminHeader: React.FC<SiteAdminHeaderProps> = ({ onMenuClick }) => {
  const { admin, logout } = useSiteAdminAuth();
  const { siteType, colors, themeConfig } = useAdminTheme();
  const { site } = useParams<{ site: string }>();
  const isStaging = import.meta.env.VITE_APP_ENV === 'staging';

  const siteName = useMemo(() => {
    if (!site) return themeConfig.siteName;
    if (site === 'super-admin') return 'Super Admin 管理ポータル';
    return getSiteAdminTheme(site).siteName;
  }, [site, themeConfig.siteName]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header
      className="text-white shadow sticky top-0 z-[1100]"
      style={{ backgroundColor: isStaging ? '#000000' : colors.primary }}
    >
      <div className="px-4">
        <div className="relative flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            {/* Menu Toggle Button */}
            <button
              onClick={onMenuClick}
              className="p-2 rounded-md hover:bg-white/20 transition-colors"
              data-testid="site-admin-menu-toggle"
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <Link to={`/${siteType}/admin`} className="text-xl font-bold text-white">
              {siteName}
            </Link>
          </div>

          {isStaging && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-4 py-1 text-lg font-bold text-black shadow-sm">
              ステージング環境
            </div>
          )}

          {/* User Menu */}
          {admin && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-white">{admin.name}</span>
              {admin.is_full_admin && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">全権限</span>
              )}
              <button
                onClick={handleLogout}
                style={{ backgroundColor: colors.secondary }}
                className="text-sm px-4 py-1.5 rounded text-white border border-white/30 transition-colors hover:opacity-90"
                data-testid="site-admin-logout-button"
              >
                ログアウト
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
